import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { eur, CHANTIER_TYPES, LIBRARY_ITEM_TYPES, TVA_RATES } from "@/lib/constants";
import { generatePdf } from "@/lib/pdf";
import LibraryPicker from "@/components/LibraryPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2, Library, Save, FileDown } from "lucide-react";

const emptyLine = { designation: "", description: "", prestation_type: "matériel", quantity: 1, unit: "U", unit_price_ht: 0, tva_rate: 0, brand: "", model: "", library_id: null };

export default function FactureEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [settings, setSettings] = useState({});
  const [f, setF] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/settings").then((r) => setSettings(r.data));
    api.get(`/factures/${id}`).then((r) => {
      if (r.data.locked || r.data.status !== "brouillon") {
        toast.error("Facture émise : édition impossible");
        navigate(`/factures/${id}`);
        return;
      }
      setF({ ...r.data, lines: r.data.lines?.length ? r.data.lines : [{ ...emptyLine }] });
    }).catch(() => { toast.error("Facture introuvable"); navigate("/factures"); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const set = (k, v) => setF((d) => ({ ...d, [k]: v }));
  const updateLine = (i, k, v) => setF((d) => { const lines = [...d.lines]; lines[i] = { ...lines[i], [k]: v }; return { ...d, lines }; });
  const addLine = () => setF((d) => ({ ...d, lines: [...d.lines, { ...emptyLine }] }));
  const removeLine = (i) => setF((d) => ({ ...d, lines: d.lines.filter((_, idx) => idx !== i) }));

  const addFromLibrary = (item) => {
    if (item?.id) api.post(`/library/${item.id}/use`).catch(() => {});
    let price = Number(item.sale_price || 0);
    if (item.pricing_mode === "sur_devis") {
      const entered = window.prompt(`Prix HT pour « ${item.designation} » (sur devis) :`, "");
      if (entered === null) return;
      price = Number(String(entered).replace(",", ".")) || 0;
    }
    const modeSuffix = item.pricing_mode === "a_partir_de" ? " (à partir de)" : item.pricing_mode === "minimum" ? " (minimum)" : "";
    setF((d) => ({
      ...d,
      lines: [...d.lines, {
        designation: (item.reference ? `[${item.reference}] ` : "") + (item.designation || "") + modeSuffix,
        description: item.description || "", prestation_type: item.item_type || "matériel",
        quantity: 1, unit: item.unit || "U", unit_price_ht: price,
        tva_rate: Number(item.tva_rate ?? 0), brand: item.brand || "", model: item.model || "", library_id: item.id,
      }],
    }));
    toast.success(`Ligne ajoutée : ${item.designation}`);
  };

  const addPack = (pack) => {
    const items = pack.items || [];
    if (!items.length) { toast.error("Ce pack est vide"); return; }
    setF((d) => ({
      ...d,
      lines: [
        ...d.lines.filter((l) => l.designation || l.library_id || Number(l.unit_price_ht) > 0),
        ...items.map((it) => ({
          designation: it.designation || "", description: it.description || "", prestation_type: it.prestation_type || "matériel",
          quantity: Number(it.quantity || 1), unit: it.unit || "U", unit_price_ht: Number(it.unit_price_ht || 0),
          tva_rate: Number(it.tva_rate ?? 0), brand: it.brand || "", model: it.model || "", library_id: it.library_id || null,
        })),
      ],
    }));
    toast.success(`Pack ajouté : ${pack.name}`);
  };

  const totals = useMemo(() => {
    if (!f) return { subtotal: 0, discount: 0, ht: 0, tva: 0, ttc: 0 };
    let subtotal = 0;
    f.lines.forEach((l) => { subtotal += Number(l.quantity || 0) * Number(l.unit_price_ht || 0); });
    const dv = Number(f.discount_value || 0);
    const discount = f.discount_type === "percent" ? subtotal * Math.max(0, Math.min(dv, 100)) / 100 : Math.max(0, Math.min(dv, subtotal));
    const ht = subtotal - discount;
    const ratio = subtotal > 0 ? ht / subtotal : 1;
    let tva = 0;
    f.lines.forEach((l) => { tva += (Number(l.quantity || 0) * Number(l.unit_price_ht || 0) * ratio * Number(l.tva_rate || 0)) / 100; });
    return { subtotal, discount, ht, tva, ttc: ht + tva };
  }, [f]);

  const buildPayload = () => ({
    client_id: f.client_id,
    chantier_address: f.chantier_address || "",
    chantier_type: f.chantier_type || "",
    chantier_type_other: f.chantier_type === "Autre" ? (f.chantier_type_other || "") : "",
    equipment: f.equipment || null,
    discount_type: f.discount_type || "percent",
    discount_value: Number(f.discount_value || 0),
    date_echeance: f.date_echeance || null,
    notes: f.notes || "",
    lines: f.lines.filter((l) => (l.designation && l.designation.trim()) || Number(l.unit_price_ht) > 0).map((l) => ({
      designation: l.designation, description: l.description || "", prestation_type: l.prestation_type,
      quantity: Number(l.quantity || 0), unit: l.unit || "U", unit_price_ht: Number(l.unit_price_ht || 0),
      tva_rate: Number(l.tva_rate || 0), brand: l.brand || "", model: l.model || "", library_id: l.library_id || null,
    })),
  });

  const save = useCallback(async (silent = false) => {
    if (!f?.client_id) { toast.error("Sélectionnez un client"); return null; }
    try {
      const { data } = await api.put(`/factures/${id}`, buildPayload());
      if (!silent) toast.success("Brouillon enregistré");
      setF({ ...data, lines: data.lines?.length ? data.lines : [{ ...emptyLine }] });
      return data;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); return null; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f, id]);

  const doPdf = async () => { const saved = await save(true); if (saved) generatePdf(saved, settings, "facture"); };

  if (!f) return <div className="p-8 text-slate-500">Chargement…</div>;

  return (
    <div className="p-4 md:p-8 space-y-6 max-w-5xl mx-auto">
      <button data-testid="back-button" onClick={() => navigate(`/factures/${id}`)} className="flex items-center gap-2 text-slate-600 font-semibold hover:text-slate-900">
        <ArrowLeft size={20} /> Retour à la facture
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">Facture (brouillon)</h1>
          <p className="text-slate-600">Issue du devis {f.devis_number || "—"}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" data-testid="facture-edit-pdf" onClick={doPdf} className="h-12 border-slate-300 font-bold"><FileDown size={18} className="mr-1" />Aperçu PDF</Button>
          <Button data-testid="facture-edit-save" onClick={() => save(false)} className="h-12 bg-[#16A34A] hover:bg-[#15803D] font-bold"><Save size={18} className="mr-1" />Enregistrer</Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Client *</Label>
          <Select value={f.client_id} onValueChange={(v) => set("client_id", v)}>
            <SelectTrigger data-testid="facture-client-select" className="h-12"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
            <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Type de chantier</Label>
          <Select value={f.chantier_type} onValueChange={(v) => set("chantier_type", v)}>
            <SelectTrigger data-testid="facture-chantier-type" className="h-12"><SelectValue /></SelectTrigger>
            <SelectContent>{CHANTIER_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          {f.chantier_type === "Autre" && (
            <Input data-testid="facture-chantier-type-other" className="h-12 mt-2" placeholder="Précisez" value={f.chantier_type_other || ""} onChange={(e) => set("chantier_type_other", e.target.value)} />
          )}
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label>Adresse chantier</Label>
          <Input data-testid="facture-address" className="h-12" value={f.chantier_address || ""} onChange={(e) => set("chantier_address", e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label>Date d'échéance</Label>
          <Input type="date" data-testid="facture-echeance-input" className="h-12" value={f.date_echeance || ""} onChange={(e) => set("date_echeance", e.target.value)} />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4 md:p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-heading text-xl font-bold">Lignes de prestation</h2>
          <Button variant="outline" data-testid="open-library-picker" onClick={() => setPickerOpen(true)} className="h-11 border-[#0284C7] text-[#0284C7] font-bold"><Library size={18} className="mr-1" />Bibliothèque</Button>
        </div>
        <div className="space-y-3">
          {f.lines.map((l, i) => {
            const lineHt = Number(l.quantity || 0) * Number(l.unit_price_ht || 0);
            return (
              <div key={i} data-testid={`facture-line-${i}`} className="border border-slate-200 rounded-md p-3 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                <div className="md:col-span-4 space-y-1"><Label className="text-xs">Désignation</Label><Input data-testid={`fline-designation-${i}`} value={l.designation} onChange={(e) => updateLine(i, "designation", e.target.value)} className="h-11" /></div>
                <div className="md:col-span-2 space-y-1">
                  <Label className="text-xs">Type</Label>
                  <Select value={l.prestation_type || undefined} onValueChange={(v) => updateLine(i, "prestation_type", v)}>
                    <SelectTrigger data-testid={`fline-type-${i}`} className="h-11"><SelectValue placeholder="Type" /></SelectTrigger>
                    <SelectContent>{(LIBRARY_ITEM_TYPES.includes(l.prestation_type) || !l.prestation_type ? LIBRARY_ITEM_TYPES : [...LIBRARY_ITEM_TYPES, l.prestation_type]).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-1 space-y-1"><Label className="text-xs">Qté</Label><Input type="number" step="0.5" data-testid={`fline-qty-${i}`} value={l.quantity} onChange={(e) => updateLine(i, "quantity", e.target.value)} className="h-11" /></div>
                <div className="md:col-span-2 space-y-1"><Label className="text-xs">P.U. HT {l.unit ? `(/${l.unit})` : ""}</Label><Input type="number" step="0.01" data-testid={`fline-price-${i}`} value={l.unit_price_ht} onChange={(e) => updateLine(i, "unit_price_ht", e.target.value)} className="h-11" /></div>
                <div className="md:col-span-2 space-y-1">
                  <Label className="text-xs">TVA</Label>
                  <Select value={String(l.tva_rate)} onValueChange={(v) => updateLine(i, "tva_rate", Number(v))}>
                    <SelectTrigger data-testid={`fline-tva-${i}`} className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>{TVA_RATES.map((t) => <SelectItem key={t.value} value={String(t.value)}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-1 flex items-center justify-between md:justify-end gap-2">
                  <span className="font-bold text-slate-900 md:hidden">{eur(lineHt)}</span>
                  <Button size="icon" variant="ghost" data-testid={`fremove-line-${i}`} onClick={() => removeLine(i)} className="text-red-600 h-11 w-11"><Trash2 size={18} /></Button>
                </div>
                <div className="hidden md:flex md:col-span-12 justify-end text-sm text-slate-500">Total ligne HT : <span className="font-bold text-slate-900 ml-1">{eur(lineHt)}</span></div>
              </div>
            );
          })}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button variant="outline" data-testid="open-library-picker-2" onClick={() => setPickerOpen(true)} className="h-11 flex-1 border-[#0284C7] text-[#0284C7] font-bold"><Library size={18} className="mr-1" />Ajouter depuis la bibliothèque</Button>
          <Button variant="outline" data-testid="add-line-button" onClick={addLine} className="h-11 flex-1 border-dashed border-slate-300 font-semibold"><Plus size={18} className="mr-1" />Ligne vide</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-md p-6 space-y-2">
          <Label>Notes</Label>
          <Textarea data-testid="facture-notes-input" value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={5} />
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-6 space-y-3">
          <div className="flex justify-between text-slate-600"><span>Sous-total HT</span><span className="font-semibold text-slate-900">{eur(totals.subtotal)}</span></div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-slate-600 shrink-0">Remise</span>
            <div className="flex items-center gap-2">
              <Input type="number" step="0.01" min="0" data-testid="facture-discount-value" value={f.discount_value || 0} onChange={(e) => set("discount_value", e.target.value)} className="h-9 w-24 text-right" />
              <Select value={f.discount_type || "percent"} onValueChange={(v) => set("discount_type", v)}>
                <SelectTrigger data-testid="facture-discount-type" className="h-9 w-20"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="percent">%</SelectItem><SelectItem value="amount">€</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          {totals.discount > 0 && <div className="flex justify-between text-[#16A34A]"><span>Remise appliquée</span><span className="font-semibold">- {eur(totals.discount)}</span></div>}
          <div className="flex justify-between text-slate-600 border-t border-slate-100 pt-2"><span>Total HT</span><span className="font-semibold text-slate-900">{eur(totals.ht)}</span></div>
          <div className="flex justify-between text-slate-600"><span>{totals.tva === 0 ? "TVA non applicable" : "TVA"}</span><span className="font-semibold text-slate-900">{eur(totals.tva)}</span></div>
          <div className="border-t border-[#0284C7] pt-3 flex justify-between items-center">
            <span className="font-heading font-bold text-lg">Total TTC</span>
            <span data-testid="facture-total-ttc" className="font-heading font-extrabold text-2xl text-[#0284C7]">{eur(totals.ttc)}</span>
          </div>
        </div>
      </div>

      <LibraryPicker open={pickerOpen} onOpenChange={setPickerOpen} onPick={addFromLibrary} onCreateBlank={addLine} onPickPack={addPack} />
    </div>
  );
}
